import {
  ENTRY_DATA_DRUG_PROFILE_GROUP_TABLE_KEY as DRUG_PROFILE,
  ENTRY_DATA_DRUG_PROFILE_SHELL_KEYS as DRUG_PROFILE_SHELL_KEYS,
  ENTRY_DATA_KEY_COMMENTS_GROUP_TABLE_KEY as KEY_COMMENTS,
  ENTRY_DATA_KEY_COMMENTS_SHELL_KEYS as KEY_COMMENTS_SHELL_KEYS,
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_FINANCING_INFORMATION_GROUP_TABLE_KEY as FINANCING_INFORMATION,
  ENTRY_DATA_FUNDED_COMPANY_GROUP_TABLE_KEY as FUNDED_COMPANY,
  ENTRY_DATA_INVESTORS_GROUP_TABLE_KEY as INVESTORS,
  ENTRY_DATA_ROUND_TYPE_AND_STATUS_GROUP_TABLE_KEY as ROUND_TYPE_AND_STATUS,
  ENTRY_DATA_FINANCING_INFORMATION_KEYS as FINANCING_INFORMATION_KEYS,
  ENTRY_DATA_FUNDED_COMPANY_KEYS as FUNDED_COMPANY_KEYS,
  ENTRY_DATA_INVESTORS_KEYS as INVESTORS_KEYS,
  ENTRY_DATA_ROUND_TYPE_AND_STATUS_KEYS as ROUND_TYPE_AND_STATUS_KEYS,
  ENTRY_DATA_ADDITIONAL_INFORMATION_VENTURE_CAPITAL_GROUP_TABLE_KEY as ADDITIONAL_INFORMATION,
  ENTRY_DATA_ADDITIONAL_INFORMATION_VENTURE_CAPITAL_KEYS as ADDITIONAL_INFORMATION_KEYS,
} from "@frontend/common";

import { ArticleEntryData } from "@/types/articleEntryData.types";

const getDrugProfileFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.ventureCapital]?.[DRUG_PROFILE];
  if (!targetPath) return [`${DRUG_PROFILE} data is missing`];
  if (!Array.isArray(targetPath)) return [`${DRUG_PROFILE} data is missing`];
  if (targetPath.length === 0) return [`${DRUG_PROFILE} data is missing`];

  const checkDrugProfileTableValues = targetPath.flatMap(
    (tableItem, tableIndex) => {
      const requiredFields = [
        {
          key: DRUG_PROFILE_SHELL_KEYS.brandName,
          label: DRUG_PROFILE_SHELL_KEYS.brandName,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.innName,
          label: DRUG_PROFILE_SHELL_KEYS.innName,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.codeName,
          label: DRUG_PROFILE_SHELL_KEYS.codeName,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.moa,
          label: DRUG_PROFILE_SHELL_KEYS.moa,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.modality,
          label: DRUG_PROFILE_SHELL_KEYS.modality,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.otherCharacteristics,
          label: DRUG_PROFILE_SHELL_KEYS.otherCharacteristics,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.roa,
          label: DRUG_PROFILE_SHELL_KEYS.roa,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.target,
          label: DRUG_PROFILE_SHELL_KEYS.target,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.targetIndication,
          label: DRUG_PROFILE_SHELL_KEYS.targetIndication,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.originatorDeveloper,
          label: DRUG_PROFILE_SHELL_KEYS.originatorDeveloper,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.genericBiosimilar,
          label: DRUG_PROFILE_SHELL_KEYS.genericBiosimilar,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.refProductName,
          label: DRUG_PROFILE_SHELL_KEYS.refProductName,
        },
        {
          key: DRUG_PROFILE_SHELL_KEYS.phase,
          label: DRUG_PROFILE_SHELL_KEYS.phase,
        },
      ];

      return requiredFields
        .map(({ key, label }) =>
          !tableItem[key]?.value
            ? `${label} is missing in ${DRUG_PROFILE} table ${tableIndex}`
            : "",
        )
        .filter((message) => !!message);
    },
  );

  return [...checkDrugProfileTableValues];
};

const getFinancingInformationFormatCheckList = (
  entryData: ArticleEntryData,
) => {
  const targetPath =
    entryData?.[SECTION_KEYS.ventureCapital]?.[FINANCING_INFORMATION];

  if (!targetPath) return [`${FINANCING_INFORMATION} data is missing`];

  const requiredFields = [
    {
      key: FINANCING_INFORMATION_KEYS.amount,
      label: FINANCING_INFORMATION_KEYS.amount,
    },
    {
      key: FINANCING_INFORMATION_KEYS.financingPurpose,
      label: FINANCING_INFORMATION_KEYS.financingPurpose,
    },
    {
      key: FINANCING_INFORMATION_KEYS.financingInformationDetail,
      label: FINANCING_INFORMATION_KEYS.financingInformationDetail,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in ${FINANCING_INFORMATION}`
          : "",
      )
      ?.filter((message) => !!message) || []
  );
};

const getFundedCompanyFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.ventureCapital]?.[FUNDED_COMPANY];

  if (!targetPath) return [`${FUNDED_COMPANY} data is missing`];

  const requiredFields = [
    {
      key: FUNDED_COMPANY_KEYS.companyDescription,
      label: FUNDED_COMPANY_KEYS.companyDescription,
    },
    {
      key: FUNDED_COMPANY_KEYS.companyName,
      label: FUNDED_COMPANY_KEYS.companyName,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in ${FUNDED_COMPANY}`
          : "",
      )
      ?.filter((message) => !!message) || []
  );
};

const getInvestorsFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.ventureCapital]?.[INVESTORS];

  if (!targetPath) return [`${INVESTORS} data is missing`];

  const requiredFields = [
    {
      key: INVESTORS_KEYS.investorName,
      label: INVESTORS_KEYS.investorName,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value ? `${label} is missing in ${INVESTORS}` : "",
      )
      ?.filter((message) => !!message) || []
  );
};

const getRoundTypeAndStatusCheckList = (entryData: ArticleEntryData) => {
  const targetPath =
    entryData?.[SECTION_KEYS.ventureCapital]?.[ROUND_TYPE_AND_STATUS];

  if (!targetPath) return [`${ROUND_TYPE_AND_STATUS} data is missing`];

  const requiredFields = [
    {
      key: ROUND_TYPE_AND_STATUS_KEYS.roundType,
      label: ROUND_TYPE_AND_STATUS_KEYS.roundType,
    },
    {
      key: ROUND_TYPE_AND_STATUS_KEYS.roundName,
      label: ROUND_TYPE_AND_STATUS_KEYS.roundName,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in ${ROUND_TYPE_AND_STATUS}`
          : "",
      )
      ?.filter((message) => !!message) || []
  );
};

const getAdditionalInformationCheckList = (entryData: ArticleEntryData) => {
  const targetPath =
    entryData?.[SECTION_KEYS.ventureCapital]?.[ADDITIONAL_INFORMATION];

  if (!targetPath) return [`${ADDITIONAL_INFORMATION} data is missing`];

  const requiredFields = [
    {
      key: ADDITIONAL_INFORMATION_KEYS.additionalInformation,
      label: ADDITIONAL_INFORMATION_KEYS.additionalInformation,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value
          ? `${label} is missing in ${ROUND_TYPE_AND_STATUS}`
          : "",
      )
      ?.filter((message) => !!message) || []
  );
};

const getKeyCommentFormatCheckList = (entryData: ArticleEntryData) => {
  const targetPath = entryData?.[SECTION_KEYS.ventureCapital]?.[KEY_COMMENTS];
  if (!targetPath) return [`${KEY_COMMENTS} data is missing`];

  const requiredFields = [
    {
      key: KEY_COMMENTS_SHELL_KEYS.quote,
      label: KEY_COMMENTS_SHELL_KEYS.quote,
    },
    {
      key: KEY_COMMENTS_SHELL_KEYS.spokesperson,
      label: KEY_COMMENTS_SHELL_KEYS.spokesperson,
    },
  ];

  return (
    requiredFields
      ?.map(({ key, label }) =>
        !targetPath[key]?.value ? `${label} is missing in ${KEY_COMMENTS}` : "",
      )
      ?.filter((message) => !!message) || []
  );
};

export const getVentureCapitalEntryDataFormatCheckList = (
  entryData: ArticleEntryData,
) => [
  ...getDrugProfileFormatCheckList(entryData),
  ...getFinancingInformationFormatCheckList(entryData),
  ...getFundedCompanyFormatCheckList(entryData),
  ...getInvestorsFormatCheckList(entryData),
  ...getRoundTypeAndStatusCheckList(entryData),
  ...getAdditionalInformationCheckList(entryData),
  ...getKeyCommentFormatCheckList(entryData),
];
